/* eslint-disable react/jsx-key */
/* eslint-disable security/detect-object-injection */
/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { removeCartExtensionProperties, updateCartExtensionProperties, updateContext } from '../../common/utils/helper';
import { reaction } from 'mobx';

import {
    cardModules,
    ICheckoutSectionContainerItem,
    ICheckoutSectionContainerModuleProps,
    ICheckoutSectionContainerViewProps,
    onAccountModules,
    Payments,
    ActionType,
    IGiftCardProperties,
    GermanPayments
} from './c-checkout-section-container';
import { ICartState, ICheckoutState } from '../c-checkout/mod/state-interfaces';
import { ICoreContext } from '@msdyn365-commerce/core-internal';
import { GetPaymentOnbillValueInput } from '../../actions/get-payment-onbill-value.action';

const ItemComponent: React.FC<ICheckoutSectionContainerItem> = ({ loading, itemProps, item }) => (
    <>
        {loading}
        <Node {...itemProps}>{item}</Node>
    </>
);

const renderRadioButtons = (
    props: ICheckoutSectionContainerModuleProps,
    selectedPayment: string,
    setSelectedPayment: React.Dispatch<React.SetStateAction<string>>
): React.ReactElement => {
    const isGerman = window.location.href.split('.').find(x => x === 'de') || window.location.href.split('/').find(x => x === 'de');
    const {
        data: { onAccountGuestCheckoutState },
        context,
        config
    } = props;
    const onSelectPaymentOption = (index: number) => {
        setSelectedPayment(config.payments[index]);
        if (onAccountGuestCheckoutState) {
            // This will directly update our application state, which should trigger all modules observing the state to update
            if (index === 1 && !onAccountGuestCheckoutState?.result?.appliedOnAccount) {
                updateContext(context, 'appliedOnAccount', true);
            } else if (onAccountGuestCheckoutState?.result?.appliedOnAccount) {
                updateContext(context, 'appliedOnAccount', false);
            }
        }
    };
    return (
        <div className='ms-checkout-delivery-options__list'>
            {config?.payments?.map((payment: string, index: number) => {
                return (
                    <div className='ms-checkout-delivery-options__option' key={index}>
                        <input
                            key={index}
                            type='radio'
                            value={selectedPayment}
                            name='paymentOptions'
                            className='ms-checkout-delivery-options__input-radio'
                            checked={selectedPayment === payment}
                            aria-checked={selectedPayment === payment}
                            onClick={() => onSelectPaymentOption(index)}
                            disabled={props.moduleState.isReady}
                        />
                        <label style={{ marginTop: 7 }} className='ms-checkout-delivery-options__description'>
                            <span>{isGerman ? GermanPayments[payment] : Payments[payment]}</span>
                        </label>
                    </div>
                );
            })}
        </div>
    );
};

const renderItems = (
    selectedPayment: string,
    items: ICheckoutSectionContainerItem[],
    props: ICheckoutSectionContainerModuleProps
): React.ReactElement | null => {
    // const checkout = props.data.checkout.result;
    return (
        <>
            {items?.map((item: ICheckoutSectionContainerItem) => {
                // if (selectedPayment === Payments[Payments.onAccountPayment]) {
                //     return <div style={item.id.includes('checkout-payment-instrument') && checkout?.customerAccountAmount! >= checkout?.checkoutCart.cart.AmountDue!  ? {display: 'none'}: {display: 'block'}}>
                //         <ItemComponent key={item.id} {...item} />
                //     </div>
                // } else if (selectedPayment === Paym ents[Payments.cardPayment]) {
                //     return <div style={item.id.includes('checkout-customer-account-payment') ? {display: 'none'}: {display: 'block'}}>
                //         <ItemComponent key={item.id} {...item} />
                //     </div>
                // }
                if (selectedPayment === Payments[Payments.onAccountPayment]) {
                    const index = onAccountModules.findIndex(x => item.id.includes(x));
                    // Previous implementation for on account without considering gift card
                    // return <div style={index >= 0 && checkout?.customerAccountAmount! >= checkout?.checkoutCart.cart.AmountDue!  ? {display: 'none'}: {display: 'block'}}>
                    //     <ItemComponent key={item.id} {...item} />
                    // </div>
                    return (
                        <div style={index >= 0 ? { display: 'none' } : { display: 'block' }}>
                            <ItemComponent key={item.id} {...item} />
                        </div>
                    );
                } else if (selectedPayment === Payments[Payments.cardPayment]) {
                    const index = cardModules.findIndex(x => item.id.includes(x));
                    return (
                        <div style={index >= 0 ? { display: 'none' } : { display: 'block' }}>
                            <ItemComponent key={item.id} {...item} />
                        </div>
                    );
                }
                return <ItemComponent key={item.id} {...item} />;
            })}
        </>
    );
};

const addPropertiesOnGiftCardChange = async (
    attributesArr: IGiftCardProperties[],
    checkoutState: ICheckoutState | undefined,
    context?: ICoreContext
) => {
    const checkoutCart: ICartState = checkoutState?.checkoutCart!;
    await updateCartExtensionProperties(checkoutCart, 'GiftCardInfo', JSON.stringify(attributesArr), context);
};

const removePropertiesOnGiftCardChange = async (checkoutState: ICheckoutState | undefined, context?: ICoreContext) => {
    const checkoutCart: ICartState = checkoutState?.checkoutCart!;
    await removeCartExtensionProperties(checkoutCart, 'GiftCardInfo', context);
};

const updateOnAccountModulesList = (
    actionType: any,
    attributesArr: IGiftCardProperties[],
    checkoutState: ICheckoutState | undefined,
    giftCardLength: number,
    rerender: boolean,
    shouldRerender: React.Dispatch<React.SetStateAction<boolean>>,
    context?: ICoreContext
) => {
    const index = onAccountModules.indexOf('checkout-customer-account-payment');
    if (index === -1 && actionType === ActionType.ADD) {
        onAccountModules.push('checkout-customer-account-payment');
        addPropertiesOnGiftCardChange(attributesArr, checkoutState);
    } else if (actionType === ActionType.REMOVE) {
        if (index >= 0) {
            onAccountModules.splice(index, 1);
            shouldRerender(!rerender);
        }
        giftCardLength === 0
            ? removePropertiesOnGiftCardChange(checkoutState, context)
            : addPropertiesOnGiftCardChange(attributesArr, checkoutState);
    }
};

const updateCardModulesList = (
    actionType: any,
    attributesArr: IGiftCardProperties[],
    checkoutState: ICheckoutState | undefined,
    giftCardLength: number,
    rerender: boolean,
    shouldRerender: React.Dispatch<React.SetStateAction<boolean>>,
    context?: ICoreContext
) => {
    const index = cardModules.indexOf('checkout-payment-instrument');
    if (index === -1 && actionType === ActionType.ADD) {
        cardModules.push('checkout-payment-instrument');
        addPropertiesOnGiftCardChange(attributesArr, checkoutState);
    } else if (actionType === ActionType.REMOVE) {
        if (index >= 0) {
            cardModules.splice(index, 1);
            shouldRerender(!rerender);
        }
        giftCardLength === 0
            ? removePropertiesOnGiftCardChange(checkoutState, context)
            : addPropertiesOnGiftCardChange(attributesArr, checkoutState);
    }
};

const CheckoutSectionContainerView: React.FC<ICheckoutSectionContainerViewProps> = ({ checkoutPlainContainerProps, items }) => {
    const { config } = checkoutPlainContainerProps.moduleProps as ICheckoutSectionContainerModuleProps;
    //const [selectedPayment, setSelectedPayment] = React.useState<string>(config.defaultPayment);
    const [selectedPayment, setSelectedPayment] = React.useState<string>(() => {
        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.has('pv') ? Payments[Payments.cardPayment] : config.defaultPayment;
    });
    const [giftCardLength, setGiftCardLength] = React.useState<number>(0);
    const [rerender, shouldRerender] = React.useState<boolean>(false);
    const props = checkoutPlainContainerProps.moduleProps as ICheckoutSectionContainerModuleProps;
    const checkoutState = props.data.checkout.result;
    let totalGiftCardBalance: number, remainingAmount: number;
    reaction(
        () => checkoutState?.giftCardExtends.length,
        () => {
            setGiftCardLength(checkoutState?.giftCardExtends.length || 0);
        }
    );
    // React.useEffect(() => {
    //     const searchParams = new URLSearchParams(window.location.search);
    //     console.log('pv query stirng', searchParams.has('pv'));
    //     if (searchParams.has('pv')) {
    //         setSelectedPayment(Payments[Payments.cardPayment]);
    //     } else {
    //         setSelectedPayment(config.defaultPayment);
    //     }
    // }, []);
    console.log('config value', config.defaultPayment, Payments.cardPayment, Payments[Payments.cardPayment]);
    React.useEffect(() => {
        totalGiftCardBalance = 0;
        remainingAmount = checkoutState?.checkoutCart.cart.AmountDue!;
        const attributesArr: IGiftCardProperties[] = [];
        for (let i = giftCardLength - 1; i >= 0; i--) {
            const card = checkoutState?.giftCardExtends[i]!;
            if (card) {
                totalGiftCardBalance += card.Balance!;
                let appliedAmount: number = 0;
                if (remainingAmount >= card.Balance!) {
                    appliedAmount = card.Balance!;
                } else if (remainingAmount < card.Balance!) {
                    appliedAmount = remainingAmount;
                }
                remainingAmount = remainingAmount - appliedAmount;
                attributesArr.push({
                    GiftCardNumber: card.Id!,
                    GiftCardAppliedAmount: appliedAmount
                });
            }
        }
        console.log('totalGiftCardBalance:::', totalGiftCardBalance);
        if (totalGiftCardBalance >= checkoutState?.checkoutCart.cart.AmountDue!) {
            if (selectedPayment === Payments[Payments.onAccountPayment]) {
                updateOnAccountModulesList(ActionType.ADD, attributesArr, checkoutState, giftCardLength, rerender, shouldRerender);
            } else if (selectedPayment === Payments[Payments.cardPayment]) {
                updateCardModulesList(ActionType.ADD, attributesArr, checkoutState, giftCardLength, rerender, shouldRerender);
            }
        } else if (totalGiftCardBalance < checkoutState?.checkoutCart.cart.AmountDue!) {
            if (selectedPayment === Payments[Payments.onAccountPayment]) {
                updateOnAccountModulesList(
                    ActionType.REMOVE,
                    attributesArr,
                    checkoutState,
                    giftCardLength,
                    rerender,
                    shouldRerender,
                    props.context
                );
            } else if (selectedPayment === Payments[Payments.cardPayment]) {
                updateCardModulesList(
                    ActionType.REMOVE,
                    attributesArr,
                    checkoutState,
                    giftCardLength,
                    rerender,
                    shouldRerender,
                    props.context
                );
            }
        }
        // console.log('props.data.checkout.result:::', props.data.checkout.result);
    }, [giftCardLength]);
    React.useEffect(() => {
        props.data.checkout.result?.updateCustomerAccountAmount({ newAmount: 0 });
        if (selectedPayment === 'cardPayment') {
            props.context.actionContext.update(new GetPaymentOnbillValueInput(), { onBill: false });
        } else {
            props.context.actionContext.update(new GetPaymentOnbillValueInput(), { onBill: true });
        }
    }, [selectedPayment]);
    return (
        <Module {...checkoutPlainContainerProps}>
            {renderRadioButtons(props, selectedPayment, setSelectedPayment)}
            {renderItems(selectedPayment, items, props)}
        </Module>
    );
};

export default CheckoutSectionContainerView;
